import { Input, ElementRef, Injector, ViewChild, HostListener, HostBinding, Component } from '@angular/core';
import { ControlValueAccessor, FormControl, FormControlDirective, ControlContainer, FormGroup } from '@angular/forms';
import { noop } from 'rxjs';
import { errorMapper } from './error.map';
import { DynamicFieldValidator, DynamicFieldValidationType } from 'src/app/lib/dynamic-form/models/dynamic-field.interface';
import { ReadOnlyDirective } from '../directives/readonly.directive';

@Component({
  template: ''
})
export class ControlValueAccessorMixin<T = any> implements ControlValueAccessor {

  @ViewChild(FormControlDirective, { static: true }) formControlDirective: FormControlDirective;

  @Input() name: string;
  @Input() formControl: FormControl;
  @Input() formControlName: string;

  isMouseOver: boolean;
  isOnFocus: boolean;
  get isRequired() {
    const control: FormControl = this.control;

    if (control && control.validator) {
      const validators = control.validator(new FormControl());

      if (validators && validators.required) {
        return true;
      } else if (validators && validators.dynamicValidator) {
        const dynamicErrors: DynamicFieldValidator[] = validators.dynamicValidator;
        return dynamicErrors.some(elem => elem.type == DynamicFieldValidationType.Required);
      }
    }

    return false;
  }

  errorParams: any;

  constructor(
    private elementRef?: ElementRef,
    private injector?: Injector,
    private readOnlyDirective?: ReadOnlyDirective
  ) {
  }

  @HostListener('mouseenter') mouseenter() {
    this.isMouseOver = true;
  }

  @HostListener('mouseleave') mouseleave() {
    this.isMouseOver = false;
  }

  // The internal data model
  // tslint:disable-next-line: member-ordering
  innerValue: T;

  // Placeholders for the callbacks which are later provided
  // by the Control Value Accessor
  // tslint:disable-next-line: member-ordering
  private onTouchedControlAccessorCallback: () => void = noop;
  // tslint:disable-next-line: member-ordering
  private onChangeControlAccessorCallback: (_: T) => void = noop;

  // get accessor
  get value(): T {
    return this.innerValue;
  }

  // Set accessor including call the onchange callback
  @Input() set value(value: T) {
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.onChangeCallback(value);
    }
  }

  // ControlValueAccessor methods
  writeValue(value: T) {
    if (value !== this.innerValue) {
      this.innerValue = value;
    }
  }

  registerOnChange(fn: (_: T) => void) {
    this.onChangeControlAccessorCallback = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouchedControlAccessorCallback = fn;
  }

  onChangeCallback(value: T) {
    this.onChangeControlAccessorCallback(value);

    if (this.elementRef) {
      const event = new Event('change');
      this.elementRef.nativeElement.value = value;
      this.elementRef.nativeElement.dispatchEvent(event, value);
    }
  }

  onTouchedCallback() {
    this.onTouchedControlAccessorCallback();
  }

  @HostBinding('class.-disabled')
  get isDisabled() {
    if (this.control) {
      return this.control.disabled;
    }
  }

  focus() {
    this.isOnFocus = true;
  }

  blur() {
    this.isOnFocus = false;
    if (this.control) {
      this.control.markAsTouched();
    }
  }

  set control(control: FormControl) {
    this.formControl = control;
    this.value = this.control.value;
  }

  get control(): FormControl {
    if (this.formControl) {
      return this.formControl;
    } else if (this.formControlName) {
      return (this.controlContainer.control as FormGroup).controls[this.formControlName] as FormControl;
    }
  }

  private get controlContainer() {
    // tslint:disable-next-line: deprecation
    return this.injector && this.injector.get(ControlContainer);
  }

  @HostBinding('class.-readonly')
  get isReadOnly() {
    return this.readOnlyDirective && this.readOnlyDirective.readonly;
  }

  get isInvalidAndTocuhed() {
    return this.control && this.control.invalid && this.control.touched;
  }

  get errors() {
    return this.control && this.control.invalid ? Object.entries(this.control.errors).map(
      ([type, param]) => this.getErrorWithParameters(type, this.control, param)) : [];
  }

  get hasErrors() {
    return this.errors.length > 0;
  }

  private getErrorWithParameters(error: string, control: FormControl, param?: any) {
    return (errorMapper.get(error) || errorMapper.get('custom'))(error, control, param);
  }

}
